import { useEffect, useMemo, useRef, useState } from "react";
import { SearchableSelect } from "./SearchableSelect";

export interface SelectOption {
  value: string;
  label: string;
}

export type FilterSpec =
  | {
      type: "search";
      key: string;
      placeholder?: string;
      value: string;
      /** Marks the filter as "hero" so it gets extra flex grow — usually the main search. */
      wide?: boolean;
    }
  | {
      type: "select";
      key: string;
      label: string;
      value: string;
      options: SelectOption[];
      /** Disable when no options exist. */
      disabled?: boolean;
    }
  | {
      type: "date";
      key: string;
      label: string;
      value: string;
    }
  | {
      type: "datetime";
      key: string;
      label: string;
      value: string;
    }
  | {
      type: "daterange";
      key: string;
      label: string;
      startKey: string;
      endKey: string;
      startValue: string;
      endValue: string;
      /** If true, input uses datetime-local; otherwise date. */
      withTime?: boolean;
    }
  | {
      type: "number";
      key: string;
      label: string;
      value: string;
      min?: number;
      max?: number;
      placeholder?: string;
    };

interface FilterBarProps {
  filters: FilterSpec[];
  onChange: (key: string, value: string) => void;
  /** Commit multiple filter values atomically (used by range filters). */
  onChangeMany?: (updates: Record<string, string | null>) => void;
  /** When provided, a compact "Clear" pill appears if any filter has a non-empty value. */
  onClear?: () => void;
  /** Optional trailing slot (e.g. "Add" button, count label). */
  trailing?: React.ReactNode;
}

/**
 * Compact horizontal filter bar used across list/analysis pages. Filters flow
 * left-to-right, wrap onto a second row on narrow viewports, and stack fully
 * on mobile. Distinct from the old `.filter-form` which stacked every filter
 * on its own row and wasted vertical space.
 */
export function FilterBar({ filters, onChange, onChangeMany, onClear, trailing }: FilterBarProps) {
  const hasActiveFilter = useMemo(
    () =>
      filters.some((f) => {
        if (f.type === "search") return f.value.trim().length > 0;
        if (f.type === "daterange") return !!(f.startValue || f.endValue);
        return f.value !== "" && f.value !== null && f.value !== undefined;
      }),
    [filters],
  );

  return (
    <div className="filter-bar" role="search">
      {filters.map((spec) => {
        if (spec.type === "search") {
          return (
            <div
              key={spec.key}
              className={`filter-bar__item filter-bar__search${spec.wide ? " filter-bar__item--wide" : ""}`}
            >
              <svg
                className="filter-bar__search-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="search"
                value={spec.value}
                placeholder={spec.placeholder}
                onChange={(e) => onChange(spec.key, e.target.value)}
                aria-label={spec.placeholder ?? "Search"}
              />
            </div>
          );
        }
        if (spec.type === "select") {
          return (
            <div key={spec.key} className="filter-bar__item filter-bar__select">
              <span className="filter-bar__label">{spec.label}</span>
              <SearchableSelect
                value={spec.value}
                options={spec.options}
                disabled={spec.disabled}
                onChange={(v) => onChange(spec.key, v)}
                ariaLabel={spec.label}
                placeholder={spec.label}
                searchPlaceholder={`Search ${spec.label.toLowerCase()}…`}
                searchable={spec.options.length > 6}
              />
            </div>
          );
        }
        if (spec.type === "date") {
          return (
            <label key={spec.key} className="filter-bar__item filter-bar__date">
              <span className="filter-bar__label">{spec.label}</span>
              <input
                type="date"
                value={spec.value}
                onChange={(e) => onChange(spec.key, e.target.value)}
              />
            </label>
          );
        }
        if (spec.type === "datetime") {
          return (
            <label key={spec.key} className="filter-bar__item filter-bar__datetime">
              <span className="filter-bar__label">{spec.label}</span>
              <input
                type="datetime-local"
                value={spec.value}
                onChange={(e) => onChange(spec.key, e.target.value)}
              />
            </label>
          );
        }
        if (spec.type === "daterange") {
          return (
            <DateRangeField
              key={spec.key}
              spec={spec}
              onApply={(updates) => {
                if (onChangeMany) {
                  onChangeMany(updates);
                } else {
                  for (const [k, v] of Object.entries(updates)) {
                    onChange(k, v ?? "");
                  }
                }
              }}
            />
          );
        }
        return (
          <label key={spec.key} className="filter-bar__item filter-bar__number">
            <span className="filter-bar__label">{spec.label}</span>
            <input
              type="number"
              value={spec.value}
              min={spec.min}
              max={spec.max}
              placeholder={spec.placeholder}
              onChange={(e) => onChange(spec.key, e.target.value)}
            />
          </label>
        );
      })}
      {hasActiveFilter && onClear && (
        <button
          type="button"
          className="filter-bar__clear"
          onClick={onClear}
          title="Reset all filters"
        >
          Clear
        </button>
      )}
      {trailing && <div className="filter-bar__trailing">{trailing}</div>}
    </div>
  );
}

type DateRangeSpec = Extract<FilterSpec, { type: "daterange" }>;

function DateRangeField({
  spec,
  onApply,
}: {
  spec: DateRangeSpec;
  onApply: (updates: Record<string, string | null>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState(spec.startValue);
  const [localEnd, setLocalEnd] = useState(spec.endValue);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => setLocalStart(spec.startValue), [spec.startValue]);
  useEffect(() => setLocalEnd(spec.endValue), [spec.endValue]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const withTime = spec.withTime !== false;

  const apply = () => {
    onApply({
      [spec.startKey]: localStart || null,
      [spec.endKey]: localEnd || null,
    });
    setOpen(false);
  };

  const clear = () => {
    setLocalStart("");
    setLocalEnd("");
    onApply({ [spec.startKey]: null, [spec.endKey]: null });
    setOpen(false);
  };

  const applyPreset = (hoursBack: number) => {
    const now = new Date();
    const then = new Date(now.getTime() - hoursBack * 3600_000);
    const s = toInputValue(then, withTime);
    const e = toInputValue(now, withTime);
    setLocalStart(s);
    setLocalEnd(e);
    onApply({ [spec.startKey]: s, [spec.endKey]: e });
    setOpen(false);
  };

  const displayText = formatRange(spec.startValue, spec.endValue, withTime);
  const hasValue = !!(spec.startValue || spec.endValue);

  return (
    <div
      ref={wrapperRef}
      className={`filter-bar__item filter-bar__daterange${open ? " is-open" : ""}`}
    >
      <span className="filter-bar__label">{spec.label}</span>
      <button
        type="button"
        className={`filter-bar__daterange-trigger${hasValue ? " has-value" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span className="filter-bar__daterange-text">{displayText}</span>
        <svg
          viewBox="0 0 24 24"
          width="10"
          height="10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="filter-bar__daterange-caret"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="filter-bar__daterange-popover" role="dialog">
          <div className="filter-bar__daterange-presets">
            <button type="button" onClick={() => applyPreset(24)}>
              Last 24h
            </button>
            <button type="button" onClick={() => applyPreset(24 * 7)}>
              Last 7d
            </button>
            <button type="button" onClick={() => applyPreset(24 * 30)}>
              Last 30d
            </button>
            <button type="button" onClick={() => applyPreset(24 * 90)}>
              Last 90d
            </button>
          </div>
          <div className="filter-bar__daterange-fields">
            <label>
              <span>From</span>
              <input
                type={withTime ? "datetime-local" : "date"}
                value={localStart}
                onChange={(e) => setLocalStart(e.target.value)}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type={withTime ? "datetime-local" : "date"}
                value={localEnd}
                onChange={(e) => setLocalEnd(e.target.value)}
              />
            </label>
          </div>
          <div className="filter-bar__daterange-actions">
            <button type="button" className="filter-bar__daterange-clear" onClick={clear}>
              Clear
            </button>
            <button type="button" className="filter-bar__daterange-apply" onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toInputValue(d: Date, withTime: boolean): string {
  const base = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return withTime ? `${base}T${pad2(d.getHours())}:${pad2(d.getMinutes())}` : base;
}

function formatRange(start: string, end: string, withTime: boolean): string {
  if (!start && !end) return "Any time";
  return `${formatSingle(start, withTime)}  →  ${formatSingle(end, withTime)}`;
}

function formatSingle(value: string, withTime: boolean): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const date = `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
  if (!withTime) return date;
  return `${date} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
